import { getIsManualReportPreviewColumn } from '@aline/common';
import type {
  ReportGridFilterItem,
  ReportGridFilterModel,
  ReportPreviewColumn,
  ToolApprovalResponse,
} from '@aline/common';
import type { QuestionRunApprovalInput } from '@aline/common/question-plan';

/** Run counts a question plan card shows and checks against the large-run limit. */
export interface QuestionPlanRunCounts {
  documentCount: number;
  questionCount: number;
}

/** "1 question", "1,200 questions". */
export function formatCount(count: number, noun: string) {
  return `${count.toLocaleString()} ${count === 1 ? noun : `${noun}s`}`;
}

/**
 * Toggles a column in or out of the plan, along with the columns tied to it:
 * leaving a column out leaves out the columns that read it, and taking one
 * back takes back the columns it reads.
 */
export function toggleExcludedIndex(
  columns: readonly ReportPreviewColumn[],
  excluded: ReadonlySet<number>,
  index: number
) {
  const next = new Set(excluded);
  const related = collectTransitiveColumnIndices({
    columns,
    followInputs: next.has(index),
    startIndex: index,
  });
  if (next.has(index)) for (const relatedIndex of related) next.delete(relatedIndex);
  else for (const relatedIndex of related) next.add(relatedIndex);
  return next;
}

export function getInputColumnTitles(
  column: ReportPreviewColumn,
  columns: readonly ReportPreviewColumn[]
) {
  const titleByKey = new Map(columns.map(planColumn => [planColumn.key, planColumn.title]));
  return column.inputColumnKeys.flatMap(key => {
    const title = titleByKey.get(key);
    return title ? [title] : [];
  });
}

export function stripDanglingInputColumnKeys(
  columns: ReportPreviewColumn[]
): ReportPreviewColumn[] {
  const keys = new Set(columns.map(column => column.key));
  return columns.map(column => ({
    ...column,
    inputColumnKeys: column.inputColumnKeys.filter(key => keys.has(key)),
  }));
}

/** The columns the user kept, with inputs to left-out columns dropped. */
export function getIncludedColumns(
  columns: readonly ReportPreviewColumn[],
  excludedIndices: ReadonlySet<number>
) {
  return stripDanglingInputColumnKeys(columns.filter((_, index) => !excludedIndices.has(index)));
}

// How many of the columns a run answers: a Manual Entry column is typed in, not run.
export function countRunnableColumns(columns: readonly ReportPreviewColumn[]) {
  return columns.filter(column => !getIsManualReportPreviewColumn(column)).length;
}

/**
 * The questions a run card lists as already existing. Approvals saved before
 * the card listed them by id carry only their titles. Those predate drafts,
 * and `questionTitles` names the drafts too, so an approval with drafts
 * never falls back to it.
 */
export function getExistingRunQuestions(input: QuestionRunApprovalInput) {
  if (input.existingQuestions.length || input.newQuestions.length) return input.existingQuestions;
  return input.questionTitles.map(title => ({ id: title, title }));
}

/** "Run" when every question exists already, "Create and run" when some are drafts. */
export function getRunPrimaryLabel(includedDraftCount: number) {
  return includedDraftCount ? 'Create and run' : 'Run';
}

/**
 * Why the run card cannot run, or undefined when it can: the org is out of AI
 * tokens, the user left out every question, or the documents under the edited
 * filters are still being counted (so a large run cannot skip its dialog).
 */
export function getRunDisabledReason({
  input,
  isCounting,
  questionCount,
}: {
  input: QuestionRunApprovalInput;
  isCounting: boolean;
  questionCount: number;
}) {
  if (input.aiUsageBlockedReason) return input.aiUsageBlockedReason;
  if (!questionCount) return 'Include at least one question to run.';
  if (isCounting) return 'Counting the matching documents...';
  return undefined;
}

/**
 * Splits a filter into the items the card's editor has a column for and the
 * rest, such as filters on question answers, which ride along unedited.
 */
export function splitEditableFilterItems(
  filterModel: ReportGridFilterModel,
  editableFields: ReadonlySet<string>
) {
  const editableItems: ReportGridFilterItem[] = [];
  const preservedItems: ReportGridFilterItem[] = [];
  for (const item of filterModel.items) {
    if (editableFields.has(item.field)) editableItems.push(item);
    else preservedItems.push(item);
  }
  return { editableItems, preservedItems };
}

/**
 * The run card's response. A skip carries nothing. A run carries the drafts
 * the user kept, the filters they set, and whether they confirmed a large
 * run; the server checks them again before anything is created.
 */
export function buildQuestionRunApprovalResponse({
  action,
  approvalId,
  excludedIndices,
  filterModel,
  input,
  largeRunConfirmed,
  messageId,
}: {
  action: 'run' | 'skip';
  approvalId: string;
  excludedIndices: ReadonlySet<number>;
  /** Undefined when the input has no filters to edit. */
  filterModel: ReportGridFilterModel | undefined;
  input: QuestionRunApprovalInput;
  largeRunConfirmed: boolean;
  messageId: string;
}): ToolApprovalResponse {
  if (action === 'skip') return { approved: false, id: approvalId, messageId };
  const { aiUsageBlockedReason: _aiUsageBlockedReason, ...approved } = input;
  return {
    approved: true,
    id: approvalId,
    input: {
      ...approved,
      ...(filterModel ? { filterModel } : {}),
      largeRunConfirmed,
      newQuestions: getIncludedColumns(input.newQuestions, excludedIndices),
    },
    messageId,
  };
}

/**
 * Walks from a column along the plan graph. `followInputs` goes to columns
 * this one reads; otherwise it goes to columns that read this one.
 */
function collectTransitiveColumnIndices({
  columns,
  followInputs,
  startIndex,
}: {
  columns: readonly ReportPreviewColumn[];
  followInputs: boolean;
  startIndex: number;
}) {
  const queue = [startIndex];
  const indices = new Set<number>([startIndex]);
  const indexByKey = new Map(columns.map((column, columnIndex) => [column.key, columnIndex]));

  while (queue.length > 0) {
    const currentIndex = queue.shift();
    const column = currentIndex === undefined ? undefined : columns[currentIndex];
    if (column === undefined) break;

    if (followInputs) {
      for (const key of column.inputColumnKeys) {
        const parentIndex = indexByKey.get(key);
        if (parentIndex !== undefined && !indices.has(parentIndex)) {
          indices.add(parentIndex);
          queue.push(parentIndex);
        }
      }
    } else {
      columns.forEach((other, otherIndex) => {
        if (!indices.has(otherIndex) && other.inputColumnKeys.includes(column.key)) {
          indices.add(otherIndex);
          queue.push(otherIndex);
        }
      });
    }
  }

  return indices;
}
